class SizeGuide extends window.BAO.CustomElement() {
  constructor() {
    super();

    this.cards = this.querySelectorAll(".size-guide-card");

    // Content refs
    this.subtitle = this.querySelector("[data-title]");
    this.tags = this.querySelector("[data-tags]");
    this.description = this.querySelector("[data-description]");
    this.bullets = this.querySelector("[data-bullets]");
    this.button = this.querySelector("[data-button]");
    this.image = this.querySelector("[data-image]");
    // Read more
    this.readMoreTrigger = this.querySelector("[data-read-more-trigger]");
    // Tabs
    this.tabs = this.querySelectorAll(".size-guide__tab");
  }

  connectedCallback() {
    this.initCards();
    this.initReadMore();
    this.initTabs();
  }

  initCards() {
    this.cards.forEach((card) => {
      card.addEventListener("click", () => {
        this.setActive(card);
      });
    });
  }

  setupReadMore() {
    const description = this.description;
    const trigger = this.readMoreTrigger;

    if (!description || !trigger) return;

    const paragraphs = description.querySelectorAll("p");
    const hasExtraContent = paragraphs.length > 1;

    description.classList.remove("is-collapsed", "is-expanded");
    trigger.hidden = !hasExtraContent;

    if (!hasExtraContent) {
      trigger.textContent = "Read more";
      trigger.setAttribute("aria-expanded", "false");
      return;
    }

    description.classList.add("is-collapsed");
    trigger.textContent = "Read more";
    trigger.setAttribute("aria-expanded", "false");
  }

  toggleReadMore() {
    const description = this.description;
    const trigger = this.readMoreTrigger;

    if (!description || !trigger) return;

    const isCollapsed = description.classList.contains("is-collapsed");

    if (isCollapsed) {
      description.classList.remove("is-collapsed");
      description.classList.add("is-expanded");
      trigger.textContent = "Read less";
      trigger.setAttribute("aria-expanded", "true");
    } else {
      description.classList.add("is-collapsed");
      description.classList.remove("is-expanded");
      trigger.textContent = "Read more";
      trigger.setAttribute("aria-expanded", "false");
    }
  }

  initReadMore() {
    if (!this.readMoreTrigger) return;

    this.readMoreTrigger.addEventListener("click", () => {
      this.toggleReadMore();
    });

    this.setupReadMore();
  }

  initTabs() {
    if (!this.tabs.length || !this.cards.length) return;

    const defaultTab = this.tabs[0].dataset.tab;

    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        const selected = tab.dataset.tab;

        // Active tab UI
        this.tabs.forEach((t) => t.setAttribute("aria-pressed", "false"));
        tab.setAttribute("aria-pressed", "true");

        let cardFocused = false;

        // Filter cards
        this.cards.forEach((card) => {
          const cardTab = card.dataset.tab;
          const isMatch = selected === defaultTab || cardTab === selected;

          // Set focus to the first matching card
          if (isMatch && !cardFocused) {
            card.focus();
            cardFocused = true;
          }

          card.classList.toggle("is-disabled", !isMatch);

          if (!isMatch) {
            card.setAttribute("aria-disabled", "true");
            card.setAttribute("tabindex", "-1");
          } else {
            card.removeAttribute("aria-disabled");
            card.removeAttribute("tabindex");
          }
        });

        const activeCard = this.querySelector(".size-guide-card.is-active");

        if (activeCard?.classList.contains("is-disabled")) {
          const firstValid = [...this.cards].find(
            (card) => !card.classList.contains("is-disabled"),
          );

          if (firstValid) {
            this.setActive(firstValid);
          }
        }
      });
    });
  }

  setActive(card) {
    // Active state
    this.cards.forEach((c) => c.classList.remove("is-active"));
    card.classList.add("is-active");

    // Parse JSON safely
    let data;
    try {
      data = JSON.parse(card.dataset.json);
      // Need to use a liquid filter to convert metafield
      data.main_image = card.dataset.mainImage;
    } catch (e) {
      console.warn("Invalid JSON on card", e);
      return;
    }

    this.updateContent(data);

    const grid = this.querySelector(".size-guide__grid");
    if (grid) {
      grid.scrollTop = 0;
    }
  }

  updateContent(data) {
    // 1. Subtitle
    if (this.subtitle) {
      this.subtitle.textContent = data.title || "";
    }

    // 2. Tags
    if (this.tags) {
      this.tags.innerHTML = (data.tags || [])
        .map((tag) => `<span>${tag}</span>`)
        .join("");
    }

    // 3. Description (Shopify richtext JSON)
    if (this.description && data.description) {
      this.description.innerHTML = this.parseRichText(data.description);
    }

    // 4. Bullets
    if (this.bullets) {
      this.bullets.innerHTML = (data.bullet_points || [])
        .map(
          (b) => `
          <li class="size-guide__bullet">
            ${this.renderTick()}
            ${b}
          </li>
        `,
        )
        .join("");
    }

    // 5. Button
    if (this.button) {
      this.button.textContent = data.button_label || "";
      this.button.href = data.button_link || "#";
    }

    // 6. Image
    if (this.image && data.main_image) {
      this.image.src = data.main_image;
    }

    this.setupReadMore();
  }

  // Convert Shopify richtext JSON → HTML
  parseRichText(richtext) {
    if (!richtext || !richtext.children) return "";

    return richtext.children
      .map((node) => {
        if (node.type === "paragraph") {
          return `<p>${node.children.map((c) => c.value).join("")}</p>`;
        }
        return "";
      })
      .join("");
  }

  setActiveFromData(data, image) {
    this.updateContent({
      ...data,
      main_image: image,
    });
  }

  updateContentWithFade(data) {
    const contentEl = this.querySelector(".size-guide__content");

    if (!contentEl) {
      this.updateContent(data);
      return;
    }

    // Only apply on mobile
    if (window.innerWidth > 1000) {
      this.updateContent(data);
      return;
    }

    contentEl.classList.add("is-fading");

    setTimeout(() => {
      this.updateContent(data);
      contentEl.classList.remove("is-fading");
    }, 150);
  }

  renderTick() {
    return `
    <svg class="size-guide__tick" width="9" height="8" viewBox="0 0 9 8" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0.351562 3.83789L2.85156 6.33789L8.35156 0.337891" stroke="#ffffff"/>
    </svg>
    `;
  }
}

customElements.define("size-guide", SizeGuide);
